import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup from "../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepSourceCards from "../../shared/ToolPrepSourceCards";
import AiFeaturePreview from "../../ai/shared/Popup/AiFeaturePreview";
import { useToolPrepImageDownload } from "../../hooks/useToolPrepImageDownload";
import {
  conversionOutputName,
  conversionRejectMessage,
  fileMatchesConversion,
  getConversionTool,
} from "../../../../config/tools/conversionTools";
import { getUploadSizeError } from "../../../../utils/uploadLimits";
import { formatFileSize } from "../../../../utils/formatFileSize";
import { registerLibraryUploadOnce } from "../../../../utils/registerLibraryUploadOnce";
import {
  convertImageFile,
  getExportErrorMessage,
} from "../../../../services/api/tools/image_convert";
import "./ImageConvertToolPrep.css";

function loadPreviewUrl(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Image Conversion prep. Same upload column and checkerboard canvas as the
 * other tools. The file is shown as uploaded; Pillow converts when the user
 * downloads or opens the result in the editor.
 */
const ImageConvertToolPrep = ({
  toolKey,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const spec = getConversionTool(toolKey);
  const { getToken, isSignedIn } = useAuth();
  const fileInputRef = useRef(null);
  const requestRef = useRef(0);
  const previewUrlRef = useRef(null);
  const convertedUrlRef = useRef(null);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [convertedUrl, setConvertedUrl] = useState(null);
  const [convertedBlob, setConvertedBlob] = useState(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);

  const revokeUrl = (url) => {
    if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const replacePreviewUrl = useCallback((next) => {
    revokeUrl(previewUrlRef.current);
    previewUrlRef.current = next;
    setPreviewUrl(next);
  }, []);

  const replaceConverted = useCallback((blob, url) => {
    revokeUrl(convertedUrlRef.current);
    convertedUrlRef.current = url;
    setConvertedBlob(blob);
    setConvertedUrl(url);
  }, []);

  useEffect(() => {
    return () => {
      revokeUrl(previewUrlRef.current);
      revokeUrl(convertedUrlRef.current);
    };
  }, []);

  const convertFile = useCallback(
    async (nextFile, requestId) => {
      const token = await getToken();
      if (!token) throw new Error("Sign in to convert this image.");
      const blob = await convertImageFile(token, nextFile, {
        sourceFormat: spec.source,
        targetFormat: spec.target,
      });
      if (requestRef.current !== requestId) return null;
      const url = URL.createObjectURL(blob);
      if (requestRef.current !== requestId) {
        revokeUrl(url);
        return null;
      }
      replaceConverted(blob, url);
      return { blob, url };
    },
    [getToken, replaceConverted, spec]
  );

  const acceptFile = useCallback(
    async (nextFile) => {
      if (!spec || !nextFile) return;
      const sizeError = getUploadSizeError(nextFile);
      if (sizeError) {
        setError(sizeError);
        return;
      }
      if (!fileMatchesConversion(nextFile, spec.key)) {
        setError(conversionRejectMessage(spec.key));
        return;
      }

      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      setError(null);
      setFile(nextFile);
      replaceConverted(null, null);
      setConverting(false);

      const preview = await loadPreviewUrl(nextFile);
      if (requestRef.current !== requestId) {
        revokeUrl(preview);
        return;
      }
      replacePreviewUrl(preview);

      try {
        const token = await getToken();
        if (token) registerLibraryUploadOnce(token, nextFile).catch(() => {});
      } catch {
        // Library save is optional.
      }

      // HEIC often cannot be drawn in the browser. Convert once so the
      // checkerboard canvas can show it; Download reuses that file.
      if (!preview) {
        setConverting(true);
        try {
          const converted = await convertFile(nextFile, requestId);
          if (!converted && requestRef.current === requestId) {
            setError(`${spec.sourceLabel} preview is not available in this browser.`);
          }
        } catch (err) {
          if (requestRef.current !== requestId) return;
          setError(await getExportErrorMessage(err));
        } finally {
          if (requestRef.current === requestId) setConverting(false);
        }
      }
    },
    [convertFile, getToken, replaceConverted, replacePreviewUrl, spec]
  );

  const clearSource = useCallback(() => {
    if (converting) return;
    requestRef.current += 1;
    setFile(null);
    replacePreviewUrl(null);
    replaceConverted(null, null);
    setError(null);
    setConverting(false);
  }, [converting, replaceConverted, replacePreviewUrl]);

  const openFilePicker = useCallback(() => {
    if (converting) return;
    fileInputRef.current?.click();
  }, [converting]);

  const ensureConverted = useCallback(async () => {
    if (convertedBlob && convertedUrl) return { blob: convertedBlob, url: convertedUrl };
    if (!file || !spec) return null;
    const requestId = requestRef.current;
    setConverting(true);
    setError(null);
    try {
      return await convertFile(file, requestId);
    } catch (err) {
      if (requestRef.current === requestId) {
        setError(await getExportErrorMessage(err));
      }
      return null;
    } finally {
      if (requestRef.current === requestId) setConverting(false);
    }
  }, [convertFile, convertedBlob, convertedUrl, file, spec]);

  const canvasUrl = previewUrl || convertedUrl;
  const { downloadBusy, onDownload, downloadFlow } = useToolPrepImageDownload(
    canvasUrl,
    file?.name || "",
    {
      enabled: Boolean(canvasUrl) && !converting,
      fallbackName: conversionOutputName(file?.name, spec?.target || "png"),
      variant: "editing",
      toolKey,
      shareTitle: spec?.title || "Image conversion",
      onError: (message) => setError(message),
    }
  );

  const handleOpenInEditor = async () => {
    if (!file || converting) return;
    const converted = await ensureConverted();
    const imageUrl = converted?.url || previewUrl;
    if (!imageUrl) return;
    onDone({ imageUrl, originalImageUrl: previewUrl || imageUrl });
  };

  if (!spec) return null;

  const left = (
    <div className="image-convert-prep">
      <input
        ref={fileInputRef}
        type="file"
        accept={spec.accept}
        className="image-convert-prep__file"
        onChange={(event) => {
          const picked = event.target.files?.[0];
          event.target.value = "";
          if (picked) void acceptFile(picked);
        }}
      />
      <ToolPrepSourceCards
        selectedKey="original"
        onSelect={() => {}}
        originalImageUrl={canvasUrl}
        previewImageUrl={canvasUrl}
        onUploadClick={openFilePicker}
        onRemove={file ? clearSource : null}
        onDropFiles={(files) => {
          const picked = files?.[0];
          if (picked) void acceptFile(picked);
        }}
        uploadLabel={spec.uploadLabel}
        uploadHint={spec.uploadHint}
        sourceName={file?.name || null}
        sourceSizeLabel={file ? formatFileSize(file.size) : null}
        actionsDisabled={converting}
      />
      {error ? <p className="image-convert-prep__error">{error}</p> : null}
      {!isSignedIn && !file ? (
        <p className="image-convert-prep__note">Sign in to convert and download.</p>
      ) : null}
    </div>
  );

  return (
    <>
      <ToolPrepPopup
        toolKey={toolKey}
        title={spec.title}
        left={left}
        hasSource={Boolean(file)}
        confirmClose={Boolean(file)}
        preview={
          <AiFeaturePreview
            imageUrl={canvasUrl}
            loading={converting && !canvasUrl}
            loadingLabel="Converting…"
            emptyLabel="Upload an image to preview"
            showSoftWatermark={Boolean(canvasUrl)}
            softWatermarkToolKey={toolKey}
          />
        }
        onCancel={onCancel}
        onDownload={onDownload}
        downloadLabel={converting ? "Converting…" : "Download"}
        downloadDisabled={!file || converting || !canvasUrl}
        onOpenInEditor={file ? handleOpenInEditor : null}
        openInEditorDisabled={!file || converting}
        showDone={false}
        busy={downloadBusy}
        footerHint={
          file
            ? `Download converts this image to ${spec.targetLabel}. Free 1K includes a light watermark.`
            : `Upload a ${spec.sourceLabel} image. Download converts it to ${spec.targetLabel}.`
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey={toolKey}
        defaultFormatId={spec.target === "jpg" ? "jpg" : "png"}
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default ImageConvertToolPrep;
